// Bevindingen per trend (dekking eindstand 25-10, punten 3 en 4). Met de
// trendlaag aan noemt een lijst van rijen uit de oude trends-tabel ze
// "bevindingen", en groepeert ze onder de trend van de trendlaag waar ze bij
// horen (GET /api/trendlaag/rust). Bevindingen die nog bij geen trend horen,
// staan er los onder. Uit = deze code wordt niet gebruikt.

import { api, type TrendlaagRust } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import { TrendRegel } from "../../stijl";
import type { KaartVerzoek } from "../kaartlaag";

export type BevindingRef = { id: string; titel: string };
export type TrendGroep = {
  trendId: string;
  titel: string;
  onderwerp: string;
  waaromNu: string | null;
  ontwikkelingen: { id: string; titel: string; werkzin: string | null }[];
  bevindingen: BevindingRef[];
};

export const meervoud = (n: number, een: string, meer: string) => `${n} ${n === 1 ? een : meer}`;

/** Zet de lijst bevindingen onder hun trend; wat nergens bij hoort, staat los. */
export function groepeer(bevindingen: readonly BevindingRef[], rust: TrendlaagRust | null): { groepen: TrendGroep[]; los: BevindingRef[] } {
  if (!rust) return { groepen: [], los: [...bevindingen] };
  const opId = new Map(bevindingen.map((b) => [b.id, b]));
  const gebruikt = new Set<string>();
  const groepen: TrendGroep[] = [];
  for (const t of rust.trends) {
    const leden = t.bevindingen.map((id) => opId.get(id)).filter((b): b is BevindingRef => !!b && !gebruikt.has(b.id));
    if (leden.length === 0) continue;
    leden.forEach((b) => gebruikt.add(b.id));
    groepen.push({ trendId: t.trendId, titel: t.titel, onderwerp: t.onderwerp, waaromNu: t.waaromNu, ontwikkelingen: t.ontwikkelingen, bevindingen: leden });
  }
  return { groepen, los: bevindingen.filter((b) => !gebruikt.has(b.id)) };
}

/** Haalt /trendlaag/rust op voor deze bevindingen (alleen als `aan`). */
export function usePerTrend(bevindingen: readonly BevindingRef[], aan = true) {
  const ids = bevindingen.map((b) => b.id);
  const sleutel = aan ? ids.join(",") : "";
  const r = useResource(() => (sleutel ? api.trendlaagRust(ids) : Promise.resolve(null)), [sleutel]);
  const { groepen, los } = groepeer(bevindingen, r.error ? null : r.data);
  return { groepen, los, laden: !!sleutel && !r.data && !r.error, fout: r.error };
}

/** De nieuwe weergave: één regel per trend (klik = de trendlaag-kaart), daaronder
 *  de bevindingen die nog bij geen trend horen (klik = hun eigen kaart). */
export function BevindingenPerTrend({ bevindingen, onOpen }: { bevindingen: readonly BevindingRef[]; onOpen: (k: KaartVerzoek) => void }) {
  const { groepen, los, laden } = usePerTrend(bevindingen);
  if (laden) return <p className="s-meta">Trends laden…</p>;
  return (
    <div className="tl-pertrend" aria-label="Bevindingen per trend">
      {groepen.map((g) => (
        <TrendRegel
          key={g.trendId}
          titel={g.titel}
          meta={[g.onderwerp, meervoud(g.bevindingen.length, "bevinding", "bevindingen")].filter(Boolean).join(" · ")}
          onOpen={() => onOpen({ soort: "laagtrend", id: g.trendId, titel: g.titel })}
        />
      ))}
      {los.length > 0 && (
        <>
          {groepen.length > 0 && <p className="s-meta">Nog bij geen trend</p>}
          {los.map((b) => (
            <TrendRegel key={b.id} titel={b.titel} meta="bevinding" onOpen={() => onOpen({ soort: "bevinding", id: b.id, titel: b.titel })} />
          ))}
        </>
      )}
    </div>
  );
}
